import { useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiErrorDetail, openDownload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Bold, Italic, Underline, List, ListOrdered, Indent, Outdent, AlignLeft, AlignCenter, AlignRight, AlignJustify, Link2, Undo2, Redo2, Mail, Send, Image as ImageIcon, CheckCircle2, Circle, Trash2, Upload, Paperclip } from "lucide-react";
import { usePagination } from "@/components/Pagination";
import { toast } from "sonner";
import { useFileNamePrompt, renamedFile } from "@/components/FileNamePrompt";

function QueueStat({ label, value, cls, id }) {
  return (
    <div className="flex flex-col" data-testid={id}>
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`text-xl font-bold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}

function QueueStatus() {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => api.get("/comunicazioni-queue/status").then((r) => setS(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (!s) return null;
  const time = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—");
  return (
    <Card className="p-4 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="comm-queue-status">
      <QueueStat label="Partite" value={s.sent} cls="text-emerald-600" id="comm-queue-sent" />
      <QueueStat label="In coda" value={s.pending} cls="text-amber-600" id="comm-queue-pending" />
      <QueueStat label="Fallite" value={s.failed} cls="text-red-500" id="comm-queue-failed" />
      <div className="text-xs text-muted-foreground sm:ml-auto space-y-0.5">
        <div data-testid="comm-queue-next">{s.running ? "Invio in corso adesso" : s.pending > 0 ? `Prossimo invio alle ${time(s.next_run_at)}` : "Nessuna email in attesa"}</div>
        <div data-testid="comm-queue-last">Ultimo invio: {s.last_sent_at ? fmtDateTime(s.last_sent_at) : "—"}</div>
      </div>
    </Card>
  );
}

const ROLE_OPTS = [
  { key: "collaboratore", label: "Collaboratori" },
  { key: "convenzionato", label: "Convenzionati" },
  { key: "segnalatore", label: "Segnalatori" },
];

export default function Comunicazioni() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [subject, setSubject] = useState("");
  const [roles, setRoles] = useState(["collaboratore", "convenzionato", "segnalatore"]);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [readStatus, setReadStatus] = useState(null);
  const [failedList, setFailedList] = useState(null);
  const [editEmails, setEditEmails] = useState({});
  const [resending, setResending] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [askName, nameDialog] = useFileNamePrompt();
  const [q, setQ] = useState("");
  const filtered = items.filter((c) => { const s = q.trim().toLowerCase(); if (!s) return true; return (c.subject || "").toLowerCase().includes(s) || (c.sent_by || "").toLowerCase().includes(s); });
  const { paged, controls } = usePagination(filtered, "comm-pagination");

  const load = useCallback(() => { api.get("/comunicazioni").then((r) => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const anyPending = items.some((c) => c.status && c.status !== "sent");
    if (!anyPending) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [items, load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };
  const FONTS = ["Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Tahoma"];
  const deleteComm = async (c) => {
    if (!c || !window.confirm(`Eliminare la comunicazione "${c.subject}"? L'azione è irreversibile.`)) return;
    try { await api.delete(`/comunicazioni/${c.id}`); toast.success("Comunicazione eliminata"); setPreview(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore eliminazione"); }
  };
  const addAttachment = async (e) => {
    let f = e.target.files?.[0]; if (fileInputRef.current) fileInputRef.current.value = ""; if (!f) return;
    const name = await askName(f.name); if (!name) return;
    f = renamedFile(f, name);
    setUploadingAtt(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/comunicazioni/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setAttachments((a) => [...a, data]); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento file"); }
    finally { setUploadingAtt(false); }
  };
  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));
  const insertImageFile = (e) => {
    const f = e.target.files?.[0]; if (imgInputRef.current) imgInputRef.current.value = ""; if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { editorRef.current?.focus(); exec("insertImage", reader.result); };
    reader.readAsDataURL(f);
  };
  const openPreview = (c) => {
    setPreview(c);
    setReadStatus(null);
    setFailedList(null);
    setEditEmails({});
    if (isAdmin) {
      api.get(`/comunicazioni/${c.id}/recipients`).then((r) => setReadStatus(r.data)).catch(() => {});
      if (c.failed_count > 0) {
        api.get(`/comunicazioni/${c.id}/failed`).then((r) => setFailedList(r.data)).catch(() => {});
      }
    }
    if (!isAdmin && !c.read) {
      api.post(`/comunicazioni/${c.id}/read`).then(() => {
        setItems((prev) => prev.map((x) => x.id === c.id ? { ...x, read: true } : x));
        window.dispatchEvent(new Event("comm-updated"));
      }).catch(() => {});
    }
  };
  const toggleRole = (r) => setRoles((p) => p.includes(r) ? p.filter((x) => x !== r) : [...p, r]);
  const markAllRead = async () => {
    try {
      await api.post("/comunicazioni/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      window.dispatchEvent(new Event("comm-updated"));
      toast.success("Tutte segnate come lette");
    } catch { toast.error("Errore"); }
  };

  const send = async () => {
    const html = editorRef.current?.innerHTML || "";
    if (!subject.trim() || !html.replace(/<[^>]*>/g, "").trim()) { toast.error("Inserisci oggetto e testo"); return; }
    if (roles.length === 0) { toast.error("Seleziona almeno un destinatario"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/comunicazioni", { subject, html, roles, attachments });
      toast.success(`In invio a ${data.total ?? data.recipient_emails?.length ?? 0} destinatari…`);
      setSubject(""); setAttachments([]); if (editorRef.current) editorRef.current.innerHTML = ""; load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSending(false);
  };

  const refreshFailed = (cid) => {
    load();
    api.get(`/comunicazioni/${cid}/failed`).then((r) => { setFailedList(r.data); setEditEmails({}); }).catch(() => {});
  };
  const doResend = async (items, okMsg) => {
    if (!preview) return;
    setResending(true);
    try {
      const { data } = await api.post(`/comunicazioni/${preview.id}/resend`, { items });
      toast.success(okMsg || `Rimesse in coda ${data.requeued} email…`);
      refreshFailed(preview.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore reinvio"); }
    setResending(false);
  };
  const resendOne = (idx, r) => {
    const newEmail = ((editEmails[idx] ?? r.email) || "").trim();
    doResend([{ recipient_id: r.recipient_id, email: r.email, new_email: newEmail }], `Reinvio a ${newEmail}…`);
  };
  const resendAll = () => {
    const items = (failedList?.failed || []).map((r, i) => ({ recipient_id: r.recipient_id, email: r.email, new_email: ((editEmails[i] ?? r.email) || "").trim() }));
    doResend(items, "Reinvio a tutti i destinatari corretti…");
  };

  return (
    <div className="space-y-6" data-testid="comunicazioni-page">
      {nameDialog}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Mail className="h-6 w-6 text-primary" /> Comunicazioni</h1>
        <p className="text-sm text-muted-foreground mt-1">{isAdmin ? "Invia comunicazioni e consulta la bacheca" : "Bacheca delle comunicazioni a te destinate"}</p>
      </div>

      {isAdmin && <QueueStatus />}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {isAdmin && (
          <div className="lg:col-span-5">
            <Card className="p-5 space-y-4" data-testid="compose-card">
              <h2 className="font-heading font-semibold">Nuova comunicazione</h2>
              <div className="space-y-1.5"><Label>Oggetto</Label><Input data-testid="comm-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
              <div className="space-y-1.5">
                <Label>Destinatari</Label>
                <div className="flex flex-wrap gap-3">
                  {ROLE_OPTS.map((o) => (
                    <label key={o.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`comm-role-${o.key}`}>
                      <Checkbox checked={roles.includes(o.key)} onCheckedChange={() => toggleRole(o.key)} /> {o.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Messaggio</Label>
                <div className="flex flex-wrap items-center gap-1 mb-1 rounded-md border border-border bg-muted/30 p-1" data-testid="comm-toolbar">
                  <select data-testid="comm-font-family" className="h-8 rounded border border-border bg-background px-1 text-xs" defaultValue="Arial" onChange={(e) => exec("fontName", e.target.value)} title="Carattere">
                    {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select data-testid="comm-font-size" className="h-8 rounded border border-border bg-background px-1 text-xs" defaultValue="3" onChange={(e) => exec("fontSize", e.target.value)} title="Dimensione">
                    <option value="1">Molto piccolo</option>
                    <option value="2">Piccolo</option>
                    <option value="3">Normale</option>
                    <option value="4">Medio</option>
                    <option value="5">Grande</option>
                    <option value="6">Molto grande</option>
                    <option value="7">Enorme</option>
                  </select>
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("bold")} title="Grassetto"><Bold className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("italic")} title="Corsivo"><Italic className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("underline")} title="Sottolineato"><Underline className="h-4 w-4" /></Button>
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("justifyLeft")} title="Allinea a sinistra"><AlignLeft className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("justifyCenter")} title="Centra"><AlignCenter className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("justifyRight")} title="Allinea a destra"><AlignRight className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("justifyFull")} title="Giustifica"><AlignJustify className="h-4 w-4" /></Button>
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("insertUnorderedList")} title="Elenco puntato"><List className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("insertOrderedList")} title="Elenco numerato"><ListOrdered className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("outdent")} title="Riduci rientro"><Outdent className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("indent")} title="Aumenta rientro"><Indent className="h-4 w-4" /></Button>
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" data-testid="comm-link-btn" onClick={() => { const url = window.prompt("URL del link:"); if (url) exec("createLink", url); }} title="Inserisci link"><Link2 className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" data-testid="comm-image-btn" onClick={() => { const url = window.prompt("URL immagine da inserire:"); if (url) exec("insertImage", url); }} title="Inserisci immagine da URL"><ImageIcon className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" data-testid="comm-image-upload-btn" onClick={() => imgInputRef.current?.click()} title="Carica immagine dal PC"><Upload className="h-4 w-4" /></Button>
                  <input ref={imgInputRef} type="file" accept="image/*" className="hidden" data-testid="comm-image-upload-input" onChange={insertImageFile} />
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("undo")} title="Annulla"><Undo2 className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => exec("redo")} title="Ripeti"><Redo2 className="h-4 w-4" /></Button>
                </div>
                <div ref={editorRef} contentEditable data-testid="comm-editor" className="min-h-[200px] rounded-md border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-primary [&_a]:underline" suppressContentEditableWarning />
              </div>
              <div className="space-y-1.5">
                <Label>Allegati (PDF, documenti)</Label>
                <input ref={fileInputRef} type="file" className="hidden" data-testid="comm-attach-input" onChange={addAttachment} />
                <Button type="button" variant="outline" size="sm" data-testid="comm-attach-btn" disabled={uploadingAtt} onClick={() => fileInputRef.current?.click()}><Paperclip className="h-4 w-4 mr-2" />{uploadingAtt ? "Caricamento…" : "Allega file"}</Button>
                {attachments.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {attachments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-2 rounded border border-border px-2 py-1 text-xs" data-testid={`comm-attach-${a.id}`}>
                        <span className="truncate">{a.original_filename}</span>
                        <button type="button" onClick={() => removeAttachment(a.id)} className="text-muted-foreground hover:text-destructive shrink-0" title="Rimuovi"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button className="w-full" data-testid="comm-send" onClick={send} disabled={sending}><Send className="h-4 w-4 mr-2" />{sending ? "Invio…" : "Invia comunicazione"}</Button>
            </Card>
          </div>
        )}

        <div className={isAdmin ? "lg:col-span-7" : "lg:col-span-12"}>
          <Card className="p-5" data-testid="bacheca-card">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-heading font-semibold">Bacheca ({filtered.length}{q ? `/${items.length}` : ""})</h2>
              {!isAdmin && items.some((c) => !c.read) && (
                <Button variant="outline" size="sm" data-testid="mark-all-read-btn" onClick={markAllRead}>Segna tutte come lette</Button>
              )}
            </div>
            <Input className="mb-3" data-testid="comm-search" placeholder="Cerca per oggetto o mittente…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="space-y-3">
              {paged.map((c) => (
                <div key={c.id} role="button" tabIndex={0} data-testid={`comm-item-${c.id}`} onClick={() => openPreview(c)} onKeyDown={(e) => { if (e.key === "Enter") openPreview(c); }} className="w-full text-left rounded-lg border border-border p-3 hover:bg-accent/40 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate flex items-center gap-2 min-w-0">{!isAdmin && !c.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" data-testid={`comm-unread-dot-${c.id}`} />}{c.subject}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] text-muted-foreground">{fmtDateTime(c.created_at)}</span>
                      {isAdmin && <button type="button" data-testid={`comm-delete-${c.id}`} title="Elimina comunicazione" onClick={(e) => { e.stopPropagation(); deleteComm(c); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Da {c.sent_by}
                    {isAdmin && c.status && c.status !== "sent"
                      ? ` · Invio in corso ${c.sent_count || 0}/${c.total || 0}${c.failed_count ? ` (${c.failed_count} falliti)` : ""}`
                      : isAdmin && ` · letta da ${c.read_count || 0}/${c.recipient_count ?? (c.recipient_emails?.length || 0)} destinatari${c.failed_count ? ` · ${c.failed_count} non recapitate` : ""}`}
                  </p>
                </div>
              ))}
              {filtered.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessuna comunicazione</p>}
            </div>
            {filtered.length > 0 && controls}
          </Card>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)} data-testid="comm-preview">
          <Card className="max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-xl font-bold">{preview.subject}</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-4">Da {preview.sent_by} · {fmtDateTime(preview.created_at)}</p>
            <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: preview.html }} />
            {preview.attachments?.length > 0 && (
              <div className="mt-4 border-t border-border pt-3" data-testid="comm-preview-attachments">
                <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Paperclip className="h-4 w-4" />Allegati ({preview.attachments.length})</p>
                <div className="space-y-1">
                  {preview.attachments.map((a) => (
                    <button type="button" key={a.id} onClick={() => openDownload(`/comunicazioni/${preview.id}/attachments/${a.id}/download`)} className="block text-left text-sm text-primary hover:underline truncate" data-testid={`comm-preview-attach-${a.id}`}>{a.original_filename}</button>
                  ))}
                </div>
              </div>
            )}
            {isAdmin && readStatus && (
              <div className="mt-6 border-t border-border pt-4" data-testid="comm-read-status">
                <p className="text-sm font-semibold mb-2">Letta da {readStatus.read_count}/{readStatus.total} destinatari</p>
                <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                  {readStatus.recipients.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`comm-recipient-${r.id}`}>
                      {r.read ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                      <span className={r.read ? "font-medium" : "text-muted-foreground"}>{r.name}</span>
                      {r.email && <span className="text-xs text-muted-foreground truncate">· {r.email}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isAdmin && failedList && failedList.count > 0 && (
              <div className="mt-6 border-t border-border pt-4" data-testid="comm-failed-status">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-sm font-semibold text-red-600">Non recapitate ({failedList.count}) — correggi l'email e reinvia</p>
                  <Button size="sm" variant="outline" disabled={resending} data-testid="comm-resend-all" onClick={resendAll}>
                    <Send className="h-3.5 w-3.5 mr-1" />Reinvia a tutti i corretti
                  </Button>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                  {failedList.failed.map((r, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`comm-failed-${i}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{r.name}</div>
                        {r.error && <div className="text-[11px] text-red-500/80 truncate">{r.error}</div>}
                      </div>
                      <Input
                        value={editEmails[i] ?? r.email}
                        onChange={(e) => setEditEmails((m) => ({ ...m, [i]: e.target.value }))}
                        className="h-8 w-56 text-sm"
                        data-testid={`comm-failed-email-${i}`}
                      />
                      <Button size="sm" variant="outline" disabled={resending} data-testid={`comm-resend-${i}`} onClick={() => resendOne(i, r)}>Reinvia</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-6 flex items-center justify-between gap-2">
              <Button variant="outline" data-testid="comm-preview-close" onClick={() => setPreview(null)}>Chiudi</Button>
              {isAdmin && <Button variant="destructive" data-testid="comm-preview-delete" onClick={() => deleteComm(preview)}><Trash2 className="h-4 w-4 mr-2" />Elimina comunicazione</Button>}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
